import {
  DatePickerComponent,
  Inject,
  MaskedDateTime,
} from '@syncfusion/ej2-react-calendars';
import clsx from 'clsx';
import './OpDatePicker.css';

/**
 * Reusable date picker component built on top of Syncfusion DatePickerComponent.
 *
 * Features:
 * - Supports masked date input
 * - Customizable date format
 * - Optional label and required asterisk
 * - Error state styling and validation message
 *
 * @param {Object} props - Component props.
 * @param {string} [props.dateFormat='dd/MM/yyyy'] - Date display and parsing format.
 * @param {string} [props.errorMessage] - Validation error message displayed below the input.
 * @param {string} [props.label] - Label displayed above the date picker.
 * @param {Object} [props.maskPlaceholder={ day: 'DD', month: 'MM', year: 'YYYY' }]
 * Mask placeholders for day, month, and year input sections.
 * @param {boolean} [props.showAsterisk] - Whether to display a required field asterisk.
 * @param {Date|string|null} [props.value] - Selected date value.
 *
 * @returns {JSX.Element} Rendered date picker component.
 */
export function OpDatePicker({
  dateFormat = 'dd/MM/yyyy',
  errorMessage,
  label,
  maskPlaceholder = { day: 'DD', month: 'MM', year: 'YYYY' },
  showAsterisk,
  value,
  ...datePickerParams
}) {
  const asterisk = showAsterisk ? '*' : '';
  // Make sure the Day and Year are in lower case
  const dateFormatDisplay = dateFormat
    .replace('DD', 'dd')
    .replace('YYYY', 'yyyy');

  return (
    <div className="op-date-picker-wrapper">
      {label && (
        <label className="op-date-picker-label">
          {label}
          <span className="op-date-picker-asterisk">{asterisk}</span>
        </label>
      )}

      <DatePickerComponent
        {...datePickerParams}
        className={clsx(
          'op-date-picker',
          errorMessage && 'op-date-picker-error-input',
        )}
        enableMask={true}
        format={dateFormatDisplay}
        maskPlaceholder={maskPlaceholder}
        value={value}
      >
        <Inject services={[MaskedDateTime]} />
      </DatePickerComponent>

      {errorMessage && (
        <p className="op-date-picker-error-message">{errorMessage}</p>
      )}
    </div>
  );
}
